import type React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import styles from './InvoicingForm.module.css';
import InputText from 'components/library/Input/InputText/InputText';
import LocationInput from 'components/library/Input/LocationInput/LocationInput';
import UserForm from './UserForm';
import isEmpty from 'utils/isEmpty';
import countryList from 'utils/countryList';
import { EU_COUNTRY_WITH_VAT } from 'global';
import type { UserState } from 'services/store/features/User/types';

type Error = {
  projectName?: string;
  address?: string;
};

interface Props {
  user: UserState;
  actions: {
    getPaymentRedirectionLink: (paymentMethod: string) => void;
    setUserInfos: () => void;
  },
  setIsCompleteSociety: (value: boolean) => void;
  setIsCompleteUser: (value: boolean) => void;
}

export default function SocietyForm({
  user, actions, setIsCompleteSociety, setIsCompleteUser
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [vatState, setVatState] = useState('ERROR');
  const [errors, setErrors] = useState<Error>({});

  const validate = () => {
    const newErrors: Error = {};
    if (user.projectName && (user.projectName.trim() === '' || user.projectName.length > 512)) {
      newErrors.projectName = t('invoicingForm.projectNameInvalid');
    }
    if (user.addressSociety.address && (user.addressSociety.address.trim() === '' || user.addressSociety.address.length > 256)) {
      newErrors.address = t('invoicingForm.addressInvalid');
    }
    if (user.addressSociety.city && (user.addressSociety.city.trim() === '' || user.addressSociety.city.length > 64)) {
      newErrors.address = t('invoicingForm.cityInvalid');
    }
    if (user.addressSociety.zip_code && (user.addressSociety.zip_code.trim() === '' || user.addressSociety.zip_code.length > 64)) {
      newErrors.address = t('invoicingForm.zipCodeInvalid');
    }
    if (user.addressSociety.country && (user.addressSociety.country.trim() === '' || user.addressSociety.country.length !== 2 || !countryList.includes(user.addressSociety.country))) {
      newErrors.address = t('');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  useEffect(() => {
    const toCheck = [
      user.projectName, user.addressSociety.address, user.addressSociety.city,
      user.addressSociety.zip_code, user.addressSociety.country,
    ];
    const completed = toCheck.map((i) => !isEmpty(i)).reduce((c, a) => c && a, true);
    if (completed && validate()) {
      setIsCompleteSociety(true);
    } else {
      setIsCompleteSociety(false);
    }
  }, [
    dispatch, user.projectName,
    user.addressSociety.address, user.addressSociety.city,
    user.addressSociety.zip_code, user.addressSociety.country,
  ]);

  useEffect(() => {
    if (user.vatVerification) {
      setVatState('DEFAULT');
    } else if (user.verifyVatNumber === 'invoicingForm.validVat') {
      setVatState('SUCCESS');
    } else {
      setVatState('ERROR');
    }
  }, [dispatch, user.vatVerification, user.verifyVatNumber]);

  const handleInputForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: e.target.name, payload: e.target.value });
  };

  const handleValidateVAT = async () => {
    actions.setUserInfos();
  };

  const debouncedValidateVAT = useCallback(
    debounce(() => handleValidateVAT(), 1000),
    [],
  );

  const handleVATField = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputForm(e);
    debouncedValidateVAT();
  };

  return (
    <>
      <h3 className={styles.title}>{t('invoicingForm.titleSociety')}</h3>
      <InputText
        className={styles.bold}
        name="UPDATE_PROJECT_NAME"
        label={t('invoicingForm.companyLegalName')}
        valueInput={user.projectName}
        onTextChange={handleInputForm}
        state={(errors?.projectName) ? 'ERROR' : 'DEFAULT'}
        hint={errors?.projectName}
        required
      />
      <LocationInput
        className={styles.bold}
        values={{ ...user.addressSociety }}
        handleChange={(e) => dispatch({ type: 'UPDATE_SOCIETY_ADDRESS', payload: { name: e.target.name, value: e.target.value } })}
        setNewValues={(name, value) => dispatch({ type: 'UPDATE_SOCIETY_ADDRESS', payload: { name, value } })}
        state={(errors?.address) ? 'ERROR' : 'DEFAULT'}
        hint={errors?.address}
      />
      {
        user.addressSociety.country !== 'FR' && EU_COUNTRY_WITH_VAT.includes(user.addressSociety.country) ? (
          <>
            <InputText
              className={`${styles.bold} ${styles.inputContainer}`}
              name="UPDATE_VAT_NUMBER"
              label={t('invoicingForm.vatNumber')}
              valueInput={user.vatNumber}
              onTextChange={handleVATField}
              state={vatState}
              hint={user.vatVerification ? (
                <>
                  <FontAwesomeIcon
                    className={styles.icon}
                    icon={faSpinner}
                    spin
                  />
                  {t('invoicingForm.vatVeritifactionInProgress')}
                </>
              ) : t(user.verifyVatNumber)}
            />
          </>
        ) : (<></>)
      }
      <UserForm user={user} setIsComplete={setIsCompleteUser} />
    </>
  );
}
